import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Target, Briefcase } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import FormDialog from "../components/FormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { FilterSelect } from "./Companies";
import CommittenteSelect, { CommittenteBadge } from "../components/CommittenteSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OPPORTUNITY_FIELDS, OPTIONS } from "../lib/entities";
import { fmtEUR, fmtDate } from "../lib/format";

export default function Opportunities() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [committente, setCommittente] = useState("all");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    if (committente !== "all") params.committente = committente;
    const { data } = await api.get("/opportunities", { params });
    setRows(data); setLoading(false);
  }, [search, status, committente]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm-refresh", h);
    return () => window.removeEventListener("crm-refresh", h);
  }, [load]);

  const remove = async () => { await api.delete(`/opportunities/${del.id}`); toast.success("Opportunità eliminata"); setDel(null); load(); };
  const toProject = async (o) => { await api.post(`/opportunities/${o.id}/convert-project`, {}); toast.success("Convertita in progetto/cliente"); load(); };

  const totalW = rows.reduce((s, o) => s + (o.weighted_value || 0), 0);
  const totalV = rows.reduce((s, o) => s + (o.value || 0), 0);

  return (
    <div>
      <PageHeader title="Opportunità" subtitle={`${rows.length} deal · Valore ${fmtEUR(totalV)} · Ponderato ${fmtEUR(totalW)}`}>
        {can("write") && <Button data-testid="add-opportunity-button" onClick={() => setCreate(true)} className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Nuova Opportunità</Button>}
      </PageHeader>

      <div className="flex flex-wrap gap-2.5 mb-4">
        <Input data-testid="opp-search" placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:w-64 h-10" />
        <CommittenteSelect mode="filter" testid="filter-opp-committente" value={committente} onChange={setCommittente} />
        <FilterSelect testid="filter-opp-status" value={status} onChange={setStatus} placeholder="Stato" options={OPTIONS.opp_status} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden table-scroll table-wide">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3 font-medium">Opportunità</th>
              <th className="px-4 py-3 font-medium">Azienda</th>
              <th className="px-4 py-3 font-medium text-right">Valore</th>
              <th className="px-4 py-3 font-medium text-right">Prob.</th>
              <th className="px-4 py-3 font-medium text-right">Ponderato</th>
              <th className="px-4 py-3 font-medium">Chiusura</th>
              <th className="px-4 py-3 font-medium">Stato</th>
              <th className="px-4 py-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Caricamento…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={8} className="px-4 py-14 text-center text-slate-400"><Target className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessuna opportunità</td></tr>
              : rows.map((o) => (
                <tr key={o.id} data-testid={`opp-row-${o.id}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-4 py-3"><p className="font-medium text-slate-800 flex items-center gap-2">{o.name}<CommittenteBadge value={o.committente} testid={`opp-committente-${o.id}`} /></p><p className="text-xs text-slate-400">{o.market || o.service || "—"}</p></td>
                  <td className="px-4 py-3 text-slate-600">{o.company?.legal_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-800">{fmtEUR(o.value)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500">{o.probability || 0}%</td>
                  <td className="px-4 py-3 text-right font-mono text-teal-700">{fmtEUR(o.weighted_value)}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{fmtDate(o.close_date)}</td>
                  <td className="px-4 py-3"><StatusBadge group="opp_status" value={o.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {can("write") && o.status === "won" && <button data-testid={`to-project-${o.id}`} onClick={() => toProject(o)} className="icon-btn p-1.5 rounded-lg hover:bg-teal-50 text-teal-600" title="Converti in progetto"><Briefcase className="w-4 h-4" /></button>}
                      {can("write") && <button data-testid={`edit-opp-${o.id}`} onClick={() => setEdit(o)} className="icon-btn p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>}
                      {can("delete") && <button data-testid={`delete-opp-${o.id}`} onClick={() => setDel(o)} className="icon-btn p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nuova Opportunità" fields={OPPORTUNITY_FIELDS} endpoint="/opportunities" onSaved={load} />
      {edit && <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title="Modifica Opportunità" fields={OPPORTUNITY_FIELDS} initial={edit} endpoint={`/opportunities/${edit.id}`} method="put" onSaved={load} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} description={`Eliminare "${del?.name}"?`} />
    </div>
  );
}
